/**
 * PLAT-230 (split from PLAT-228): the /api/me session probe behind SessionProvider.
 *
 * Kept out of the React hook so the outcome rules can be tested without a
 * DOM. The rules:
 *   - 200 → authenticated.
 *   - 503 `db_unavailable` (the server could not reach the database: see
 *     withErrorEnvelope in functions/_lib/auth-context.ts) → a recoverable
 *     outage. The session is neither verified nor rejected, so the probe
 *     waits and asks again. The caller stays in 'loading' meanwhile: no
 *     access is granted on an unverified session, and the user is not
 *     signed out because the database blinked.
 *   - anything else (401, other 5xx, network error) or an attempt that
 *     outlives SESSION_FETCH_BUDGET_MS → unauthenticated, as before.
 */

import { ApiError } from '../lib/api/_shared.ts';
import type { CurrentUser } from '../lib/types.ts';

/**
 * Per-attempt ceiling. Sub-agent NO-ABORT (2026-08-13) relaxed it 3s → 15s
 * so a cold Neon compute finishing its /get-session round-trip (~2-8s p95)
 * isn't mistaken for an expired session. Beyond 15s a real stall exists and
 * falling back to unauthenticated is the right recovery.
 */
export const SESSION_FETCH_BUDGET_MS = 15_000;

/** First wait after a 503 db_unavailable; matches the server's `Retry-After: 1`. */
export const DB_UNAVAILABLE_RETRY_BASE_MS = 1_000;

/** Backoff ceiling: the wait doubles per consecutive 503 up to this. */
export const DB_UNAVAILABLE_RETRY_MAX_MS = 10_000;

export type SessionProbeOutcome =
  | { kind: 'authenticated'; user: CurrentUser }
  | { kind: 'unauthenticated' }
  | { kind: 'aborted' };

/** True for the server's retryable "database unreachable" answer. */
export function isDbUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 503 && err.code === 'db_unavailable';
}

/** Wait before the next attempt after `attempt` consecutive 503s (1-based). */
export function dbUnavailableRetryDelayMs(attempt: number): number {
  const exp = DB_UNAVAILABLE_RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1);
  return Math.min(DB_UNAVAILABLE_RETRY_MAX_MS, exp);
}

/** Resolves after `ms`, or as soon as `signal` aborts. */
function waitOrAbort(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const id = setTimeout(done, ms);
    function done() {
      clearTimeout(id);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done);
  });
}

type AttemptResult =
  | { kind: 'ok'; user: CurrentUser }
  | { kind: 'error'; err: unknown }
  | { kind: 'timeout' };

/** One /api/me attempt, capped at `budgetMs`. */
function attemptOnce(
  fetchUser: () => Promise<CurrentUser>,
  budgetMs: number,
): Promise<AttemptResult> {
  return new Promise((resolve) => {
    const id = setTimeout(() => resolve({ kind: 'timeout' }), budgetMs);
    fetchUser().then(
      (user) => {
        clearTimeout(id);
        resolve({ kind: 'ok', user });
      },
      (err: unknown) => {
        clearTimeout(id);
        resolve({ kind: 'error', err });
      },
    );
  });
}

/**
 * Probe /api/me until it gives a definite answer. Retries a 503
 * db_unavailable with capped exponential backoff for as long as the caller
 * keeps `signal` open (SessionProvider aborts it on unmount or the next
 * probe), so an outage of any length ends in the user's real session state,
 * never in a spurious sign-out.
 */
export async function probeSession(
  fetchUser: () => Promise<CurrentUser>,
  signal: AbortSignal,
  budgetMs: number = SESSION_FETCH_BUDGET_MS,
): Promise<SessionProbeOutcome> {
  let outages = 0;
  for (;;) {
    if (signal.aborted) return { kind: 'aborted' };
    const result = await attemptOnce(fetchUser, budgetMs);
    if (signal.aborted) return { kind: 'aborted' };
    if (result.kind === 'ok') return { kind: 'authenticated', user: result.user };
    if (result.kind === 'error' && isDbUnavailable(result.err)) {
      outages += 1;
      await waitOrAbort(dbUnavailableRetryDelayMs(outages), signal);
      continue;
    }
    return { kind: 'unauthenticated' };
  }
}
